import type { ApiError, AuthTokens } from '@tb/shared';
import { API_URL } from '../config';

const ACCESS_KEY = 'tb.access';
const REFRESH_KEY = 'tb.refresh';

export class RequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'RequestError';
  }
}

export const tokenStore = {
  get access(): string | null {
    return safeRead(ACCESS_KEY);
  },
  get refresh(): string | null {
    return safeRead(REFRESH_KEY);
  },
  set(tokens: AuthTokens): void {
    safeWrite(ACCESS_KEY, tokens.accessToken);
    safeWrite(REFRESH_KEY, tokens.refreshToken);
  },
  clear(): void {
    safeWrite(ACCESS_KEY, null);
    safeWrite(REFRESH_KEY, null);
  },
};

function safeRead(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeWrite(key: string, value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {}
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  anonymous?: boolean;
  raw?: boolean;
  signal?: AbortSignal;
}

let refreshInFlight: Promise<boolean> | null = null;

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const sentWith = options.anonymous ? null : tokenStore.access;
  const response = await send(path, options, sentWith);

  if (response.status === 401 && !options.anonymous) {
    const refreshed = await refreshTokens();
    if (refreshed) {
      const retry = await send(path, options, tokenStore.access);
      return finish<T>(retry, options);
    }

    if (sentWith !== null && tokenStore.access === sentWith) tokenStore.clear();
  }

  return finish<T>(response, options);
}

async function send(
  path: string,
  options: RequestOptions,
  token: string | null,
): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json' };

  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (token) headers.Authorization = `Bearer ${token}`;

  return fetch(`${API_URL}/api${path}`, {
    method: options.method ?? 'GET',
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    ...(options.signal ? { signal: options.signal } : {}),
  });
}

async function finish<T>(response: Response, options: RequestOptions): Promise<T> {
  if (!response.ok) throw await toError(response);
  if (response.status === 204) return undefined as T;
  if (options.raw) return (await response.text()) as T;
  return (await response.json()) as T;
}

async function toError(response: Response): Promise<RequestError> {
  let body: Partial<ApiError> = {};
  try {
    body = (await response.json()) as Partial<ApiError>;
  } catch {}

  return new RequestError(
    response.status,
    body.code ?? 'HTTP_ERROR',
    body.message ?? `Request failed with status ${response.status}.`,
    body.details,
  );
}

async function refreshTokens(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    const refreshToken = tokenStore.refresh;
    if (!refreshToken) return false;

    try {
      const response = await fetch(`${API_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!response.ok) return false;

      tokenStore.set((await response.json()) as AuthTokens);
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

export async function uploadFile<T>(path: string, file: File): Promise<T> {
  const form = new FormData();
  form.append('file', file);

  const headers: Record<string, string> = {};
  const token = tokenStore.access;
  if (token) headers.Authorization = `Bearer ${token}`;

  const response = await fetch(`${API_URL}/api${path}`, {
    method: 'POST',
    headers,
    body: form,
  });

  if (!response.ok) throw await toError(response);
  return (await response.json()) as T;
}
